import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { Button } from "./Button";

type Tone = "default" | "danger" | "brand";

export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
}

type Resolver = (ok: boolean) => void;

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<Resolver | null>(null);

  const confirm = useCallback((next: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setOpts(next);
    });
  }, []);

  const finish = useCallback((ok: boolean) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    r?.(ok);
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {opts && <ConfirmDialog opts={opts} onCancel={() => finish(false)} onConfirm={() => finish(true)} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
}

function ConfirmDialog({
  opts,
  onCancel,
  onConfirm,
}: {
  opts: ConfirmOptions;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter") onConfirm();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel, onConfirm]);

  const tone: Tone = opts.tone ?? "default";
  const confirmLabel = opts.confirmLabel ?? (tone === "danger" ? "Удалить" : "Подтвердить");
  const cancelLabel = opts.cancelLabel ?? "Отмена";
  const confirmStyle: React.CSSProperties =
    tone === "danger"
      ? { background: "var(--fg-danger)", color: "#fff" }
      : {};

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        animation: "sp-fade-in var(--d-med) var(--ease-out)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--bg)",
          borderRadius: "var(--r-lg)",
          padding: 20,
          boxShadow: "0 24px 64px rgba(0,0,0,0.24)",
          animation: "sp-fade-in var(--d-slow) var(--ease-out)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <h2 className="h2" style={{ margin: 0 }}>{opts.title}</h2>
        {opts.description && (
          <div className="body" style={{ color: "var(--fg2)" }}>{opts.description}</div>
        )}

        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <Button size="sm" variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
          <Button size="sm" variant="primary" onClick={onConfirm} style={confirmStyle} autoFocus>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
